import { motion } from "framer-motion";
import { Waves, Sunset, Baby, Timer } from "lucide-react";
import { useT } from "../lib/i18n";

const TOUR_IMG =
  "https://images.unsplash.com/photo-1512936702668-1ab037aced2a?auto=format&fit=crop&w=1600&q=80";
const ACTION_IMG =
  "https://images.pexels.com/photos/4719493/pexels-photo-4719493.jpeg?auto=compress&cs=tinysrgb&w=1600";

function Cell({ c, i }) {
  const Icon = c.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: i * 0.04 }}
      className={`${c.span} relative min-h-[180px] md:min-h-[230px] overflow-hidden border border-white/10 ${
        c.kind === "image"
          ? ""
          : c.accent
          ? "bg-[color:var(--primary)] text-white"
          : "bg-[color:var(--bg-elev)] text-white"
      } ${c.kind === "stat" ? "p-6 md:p-7 flex flex-col justify-between" : ""}`}
      data-testid={`tours-cell-${i}`}
    >
      {c.kind === "image" ? (
        <>
          <img
            src={c.src}
            alt={c.label}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-8 text-white">
            <p className="overline text-[color:var(--primary)] mb-2">{c.label}</p>
            <p className="font-display text-2xl md:text-3xl leading-[0.95] max-w-md">{c.caption}</p>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between">
            {Icon && <Icon size={22} strokeWidth={1.5} />}
            <p className="text-[9px] uppercase tracking-[0.28em] font-mono opacity-80 text-right">
              {c.label}
            </p>
          </div>
          <div>
            <p className="font-display text-6xl md:text-7xl leading-none">{c.big}</p>
            <p className="mt-3 text-xs md:text-sm leading-relaxed opacity-90 max-w-xs">{c.body}</p>
          </div>
        </>
      )}
    </motion.div>
  );
}

export default function TourInfo() {
  const { t } = useT();
  const cells = [
    {
      span: "md:col-span-5 md:row-span-2",
      kind: "image",
      src: TOUR_IMG,
      label: t("tourInfo.cells.beach.label"),
      caption: t("tourInfo.cells.beach.caption"),
    },
    {
      span: "md:col-span-4",
      kind: "stat",
      big: t("tourInfo.cells.flagship.big"),
      label: t("tourInfo.cells.flagship.label"),
      body: t("tourInfo.cells.flagship.body"),
      icon: Waves,
      accent: true,
    },
    {
      span: "md:col-span-3",
      kind: "stat",
      big: t("tourInfo.cells.night.big"),
      label: t("tourInfo.cells.night.label"),
      body: t("tourInfo.cells.night.body"),
      icon: Sunset,
      accent: false,
    },
    {
      span: "md:col-span-3",
      kind: "stat",
      big: t("tourInfo.cells.ageAtv.big"),
      label: t("tourInfo.cells.ageAtv.label"),
      body: t("tourInfo.cells.ageAtv.body"),
      icon: Baby,
      accent: false,
    },
    {
      span: "md:col-span-4",
      kind: "stat",
      big: t("tourInfo.cells.ageUtv.big"),
      label: t("tourInfo.cells.ageUtv.label"),
      body: t("tourInfo.cells.ageUtv.body"),
      icon: Timer,
      accent: true,
    },
    {
      span: "md:col-span-5",
      kind: "image",
      src: ACTION_IMG,
      label: t("tourInfo.cells.fullSend.label"),
      caption: t("tourInfo.cells.fullSend.caption"),
    },
  ];

  return (
    <section
      id="tour-info"
      className="bg-black py-20 md:py-28 border-t border-white/10"
      data-testid="tour-info-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 items-end gap-6 mb-10 md:mb-14">
          <div className="md:col-span-8">
            <p className="overline mb-3">{t("tourInfo.overline")}</p>
            <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
              {t("tourInfo.heading1")}
              <br />
              <span className="text-[color:var(--primary)]">{t("tourInfo.heading2")}</span>
            </h2>
          </div>
          <div className="md:col-span-4 text-white/60 text-sm md:text-base">
            {t("tourInfo.subhead")}
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-3 md:gap-4 auto-rows-[180px] md:auto-rows-[230px]">
          {cells.map((c, i) => (
            <Cell key={i} c={c} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
